import React from 'react';

const fmtMoney = (v: any) =>
  v == null ? '—' : Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtPct = (v: any) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b py-1.5 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={accent ? 'font-semibold text-primary' : 'font-medium'}>{value}</span>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {children}
    </div>
  );
}

function FeasibilityBadge({ feasibility }: { feasibility?: string }) {
  const color =
    feasibility === 'on-track' ? 'bg-emerald-100 text-emerald-700'
    : feasibility === 'infeasible' ? 'bg-rose-100 text-rose-700'
    : 'bg-amber-100 text-amber-700';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>{feasibility || '—'}</span>;
}

function AllocationBar({ label, value }: { label: string; value?: number }) {
  const width = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <div className="mb-1.5">
      <div className="mb-0.5 flex justify-between text-xs">
        <span>{label}</span>
        <span className="font-medium">{fmtPct(width)}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded bg-muted">
        <div className="h-full rounded bg-primary" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

/* ============ Financial Planning ============ */

function CashFlowCard({ cashFlow }: { cashFlow: any }) {
  if (!cashFlow) return null;
  return (
    <Card title="Cash Flow">
      <Row label="Monthly income" value={fmtMoney(cashFlow.monthlyIncome)} />
      <Row label="Monthly expenses" value={fmtMoney(cashFlow.monthlyExpenses)} />
      <Row label="Debt obligations" value={fmtMoney(cashFlow.debtObligations)} />
      <Row label="Savings rate" value={fmtPct(cashFlow.savingsRate)} accent />
    </Card>
  );
}

function GoalsCard({ goals }: { goals?: any[] }) {
  if (!goals || goals.length === 0) return null;
  return (
    <Card title="Goals">
      {goals.map((goal: any, i: number) => (
        <div key={goal.id || i} className="mb-2 rounded-lg bg-muted/50 p-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm font-medium">{goal.name}</span>
            <FeasibilityBadge feasibility={goal.feasibility} />
          </div>
          <div className="text-xs text-muted-foreground">
            Target {fmtMoney(goal.targetAmount)} by {goal.targetDate || '—'} · contribution{' '}
            <span className="font-medium text-foreground">{fmtMoney(goal.monthlyContribution)}/mo</span>
            {goal.gap ? <span> · gap {fmtMoney(goal.gap)}</span> : null}
          </div>
        </div>
      ))}
    </Card>
  );
}

function BudgetCard({ budget }: { budget?: any[] }) {
  if (!budget || budget.length === 0) return null;
  return (
    <Card title="Budget">
      {budget.map((cat: any, i: number) => (
        <Row key={i} label={cat.category} value={`${fmtMoney(cat.planned)} / ${fmtMoney(cat.actual)}`} />
      ))}
    </Card>
  );
}

function AlertsCard({ alerts }: { alerts?: any[] }) {
  if (!alerts || alerts.length === 0) return null;
  return (
    <Card title="Alerts">
      {alerts.map((alert: any, i: number) => (
        <div key={i} className="mb-1.5 flex items-start gap-2 text-sm last:mb-0">
          <span
            className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
              alert.severity === 'high' ? 'bg-rose-500' : alert.severity === 'medium' ? 'bg-amber-500' : 'bg-sky-500'
            }`}
          />
          <span>{alert.title}</span>
        </div>
      ))}
    </Card>
  );
}

/* ============ Investment ============ */

function AllocationCard({ target, current }: { target?: any; current?: any }) {
  if (!target || !current) return null;
  const classes = Array.from(new Set([...Object.keys(target), ...Object.keys(current)]));
  return (
    <Card title="Allocation (target vs current)">
      {classes.map((c) => (
        <div key={c} className="flex items-center gap-2">
          <div className="w-full">
            <AllocationBar label={`${c} (target)`} value={target[c]} />
            <AllocationBar label={`${c} (current)`} value={current[c]} />
          </div>
        </div>
      ))}
    </Card>
  );
}

function DriftCard({ drift }: { drift?: any[] }) {
  if (!drift || drift.length === 0) return null;
  return (
    <Card title="Drift">
      {drift.map((d: any, i: number) => (
        <div key={i} className="mb-2 rounded-lg bg-muted/50 p-2.5 text-sm last:mb-0">
          <div className="flex items-center justify-between">
            <span className="font-medium">{d.assetClass}</span>
            <span className={Number(d.deviation) > 0 ? 'text-rose-600' : 'text-sky-600'}>
              {Number(d.deviation) > 0 ? '+' : ''}{d.deviation} pts
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            target {fmtPct(d.targetPercent)} · current {fmtPct(d.currentPercent)}
          </div>
        </div>
      ))}
    </Card>
  );
}

function TradesCard({ trades }: { trades?: any[] }) {
  if (!trades || trades.length === 0) return null;
  return (
    <Card title="Proposed Trades (DRAFT)">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th className="pb-1.5 font-medium">Security</th>
            <th className="pb-1.5 font-medium">Side</th>
            <th className="pb-1.5 text-right font-medium">Est. amount</th>
            <th className="pb-1.5 font-medium">Reason</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t: any, i: number) => (
            <tr key={i} className="border-t">
              <td className="py-1.5 font-medium">{t.security}</td>
              <td className={`py-1.5 ${t.side === 'sell' ? 'text-rose-600' : 'text-emerald-600'}`}>
                {t.side?.toUpperCase()}
              </td>
              <td className="py-1.5 text-right">{fmtMoney(t.estimatedAmount)}</td>
              <td className="py-1.5 text-muted-foreground">{t.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/* ============ Retirement & Tax ============ */

function ProjectionCard({ projection }: { projection: any }) {
  if (!projection) return null;
  const success = Number(projection.successProbability) || 0;
  return (
    <Card title="Retirement Projection">
      <div className="mb-3 flex items-center gap-3">
        <div className="relative h-16 w-16">
          <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="currentColor" className="text-muted" strokeWidth="4" />
            <circle
              cx="18"
              cy="18"
              r="15.9"
              fill="none"
              stroke="currentColor"
              className="text-primary"
              strokeWidth="4"
              strokeDasharray={`${success}, 100`}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
            {success.toFixed(0)}%
          </span>
        </div>
        <div className="space-y-1 text-sm">
          <div>
            <span className="text-muted-foreground">Projected balance:</span>{' '}
            <span className="font-medium">{fmtMoney(projection.projectedBalanceAtRetirement)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Annual withdrawal:</span>{' '}
            <span className="font-medium">{fmtMoney(projection.annualWithdrawal)}</span>
          </div>
          {projection.shortfall ? (
            <div className="text-rose-600">Shortfall: {fmtMoney(projection.shortfall)}</div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function WithdrawalCard({ plan }: { plan?: any[] }) {
  if (!plan || plan.length === 0) return null;
  return (
    <Card title="Withdrawal Plan">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th className="pb-1.5 font-medium">Year</th>
            <th className="pb-1.5 font-medium">Source</th>
            <th className="pb-1.5 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {plan.map((w: any, i: number) => (
            <tr key={i} className="border-t">
              <td className="py-1.5">{w.year}</td>
              <td className="py-1.5">{w.source}</td>
              <td className="py-1.5 text-right">{fmtMoney(w.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function TaxStrategyCard({ taxStrategy }: { taxStrategy?: any[] }) {
  if (!taxStrategy || taxStrategy.length === 0) return null;
  return (
    <Card title="Tax Strategy">
      {taxStrategy.map((t: any, i: number) => (
        <Row key={i} label={`${t.action} ${t.security || ''}`.trim()} value={fmtMoney(t.estimatedTaxImpact)} />
      ))}
    </Card>
  );
}

/* ============ Estate & Risk ============ */

function NetWorthCard({ netWorth }: { netWorth: any }) {
  if (!netWorth) return null;
  return (
    <Card title="Net Worth">
      <Row label="Liquid assets" value={fmtMoney(netWorth.liquidAssets)} />
      <Row label="Illiquid assets" value={fmtMoney(netWorth.illiquidAssets)} />
      <Row label="Liabilities" value={fmtMoney(netWorth.liabilities)} />
      <Row label="Total net worth" value={fmtMoney(netWorth.totalNetWorth)} accent />
    </Card>
  );
}

function InsuranceCard({ insuranceNeeds }: { insuranceNeeds?: any[] }) {
  if (!insuranceNeeds || insuranceNeeds.length === 0) return null;
  return (
    <Card title="Insurance Needs">
      {insuranceNeeds.map((n: any, i: number) => {
        const coverage = Number(n.currentCoverage) || 0;
        const recommended = Number(n.recommendedCoverage) || 0;
        const pct = recommended > 0 ? Math.min(100, (coverage / recommended) * 100) : 0;
        return (
          <div key={i} className="mb-3 last:mb-0">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="font-medium capitalize">{n.type}</span>
              <span className={Number(n.gap) > 0 ? 'text-rose-600' : 'text-emerald-600'}>
                gap {fmtMoney(n.gap)}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded bg-muted">
              <div
                className="h-full rounded bg-primary"
                style={{ width: `${Math.max(pct, 3)}%` }}
              />
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {fmtMoney(n.currentCoverage)} of {fmtMoney(n.recommendedCoverage)} covered
            </div>
          </div>
        );
      })}
    </Card>
  );
}

function EstatePlanCard({ estatePlan }: { estatePlan: any }) {
  if (!estatePlan) return null;
  return (
    <Card title="Estate Plan (DRAFT)">
      {estatePlan.beneficiaries?.length ? (
        <div className="mb-2">
          <div className="mb-1 text-xs font-medium text-muted-foreground">Beneficiaries</div>
          {estatePlan.beneficiaries.map((b: any, i: number) => (
            <Row key={i} label={`${b.name} (${b.relation})`} value={fmtPct((b.share || 0) * 100)} />
          ))}
        </div>
      ) : null}
      {estatePlan.vehicles?.length ? (
        <div className="mb-2">
          <div className="mb-1 text-xs font-medium text-muted-foreground">Vehicles</div>
          <div className="flex flex-wrap gap-1.5">
            {estatePlan.vehicles.map((v: string, i: number) => (
              <span key={i} className="rounded-full bg-muted px-2 py-0.5 text-xs">
                {v}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {estatePlan.estateTaxEstimate != null ? (
        <Row label="Est. estate tax" value={fmtMoney(estatePlan.estateTaxEstimate)} />
      ) : null}
    </Card>
  );
}

/* ============ Dispatcher ============ */

export function renderAgentCards(domain: string, data: any) {
  if (!data) return null;
  switch (domain) {
    case 'financial-planning':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <CashFlowCard cashFlow={data.cashFlow} />
          <GoalsCard goals={data.goals} />
          <BudgetCard budget={data.budget} />
          <AlertsCard alerts={data.alerts} />
        </div>
      );
    case 'investment':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <AllocationCard target={data.targetAllocation} current={data.currentAllocation} />
          <DriftCard drift={data.drift} />
          <div className="md:col-span-2">
            <TradesCard trades={data.trades} />
          </div>
        </div>
      );
    case 'retirement-tax':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <ProjectionCard projection={data.projection} />
          <WithdrawalCard plan={data.withdrawalPlan} />
          <div className="md:col-span-2">
            <TaxStrategyCard taxStrategy={data.taxStrategy} />
          </div>
        </div>
      );
    case 'estate-risk':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <NetWorthCard netWorth={data.netWorth} />
          <InsuranceCard insuranceNeeds={data.insuranceNeeds} />
          <div className="md:col-span-2">
            <EstatePlanCard estatePlan={data.estatePlan} />
          </div>
        </div>
      );
    default:
      return null;
  }
}